import { getFullMarketContext } from '@theme/market-context';
import { scheduleCartDeliveryEtaSummaryRefresh } from '@theme/delivery-location-eta';

function getStoredMarket() {
  try {
    const full = getFullMarketContext();
    return full && Object.keys(full).length ? full : null;
  } catch (_) {
    return null;
  }
}

function getActiveDeliveryLocation() {
  let stored = null;
  try {
    stored = getStoredMarket();
  } catch (_) {
    stored = null;
  }
  const picker = document.querySelector('[data-delivery-pickup]');
  const marketCode = ((picker && picker.dataset && picker.dataset.currentMarketCode) || window.theme?.defaultCountry || 'AE').toUpperCase();
  const storedCode = ((stored && (stored.countryCode || stored.country)) || '').toUpperCase();
  const useStored = storedCode && storedCode === marketCode;
  const display = document.querySelector('[data-delivery-location-display]');
  return {
    countryCode: useStored ? storedCode : marketCode,
    city: useStored ? String(stored.city || '').trim() : '',
    displayText: display ? (display.textContent || '').trim() : ''
  };
}

function getFreeDeliveryThresholdMinor() {
  const location = getActiveDeliveryLocation();
  const code = location.countryCode;
  const cityText = (location.city || location.displayText || '').toLowerCase();
  if (code === 'AE') return !cityText || cityText.includes('dubai') ? 0 : 50000;
  if (['SA', 'KW', 'QA', 'OM', 'BH'].includes(code)) return 100000;
  return null;
}

function formatMinorPrice(minor) {
  if (window.formatMinorPrice) return window.formatMinorPrice(minor);
  return (minor / 100).toFixed(2);
}

function updateFreeDeliveryProgress(subtotalMinor, deliveryMinor, rateLoaded, currency) {
  const progress = document.getElementById('cart-free-delivery-progress');
  if (!progress) return;
  const thresholdMinor = getFreeDeliveryThresholdMinor();
  if (thresholdMinor === 0) {
    progress.hidden = true;
    return;
  }
  const hasUnlocked = (rateLoaded && deliveryMinor === 0) || (thresholdMinor != null && subtotalMinor >= thresholdMinor);
  if (thresholdMinor == null && !hasUnlocked) {
    progress.hidden = true;
    return;
  }
  progress.hidden = false;
  const remainingWrap = progress.querySelector('[data-free-delivery-remaining]');
  const amountWrap = progress.querySelector('[data-free-delivery-amount]');
  const bar = progress.querySelector('[data-free-delivery-bar]');
  if (hasUnlocked) {
    progress.hidden = true;
    return;
  }
  const remainingMinor = Math.max(thresholdMinor - subtotalMinor, 0);
  const percent = thresholdMinor > 0 ? Math.min(100, Math.max(0, (subtotalMinor / thresholdMinor) * 100)) : 100;
  if (remainingWrap) remainingWrap.hidden = false;
  if (amountWrap) {
    amountWrap.dataset.priceMinor = String(remainingMinor);
    amountWrap.dataset.currency = currency;
    const valueEl = amountWrap.querySelector('[data-price-value]');
    if (valueEl) valueEl.textContent = formatMinorPrice(remainingMinor);
  }
  if (bar) bar.style.width = percent + '%';
}

function updatePriceElement(el, minor, currency, serverTxt, allSelected) {
  if (!el) return;
  el.dataset.priceMinor = String(minor);
  el.dataset.originalPrice = String(minor);
  el.dataset.originalCurrency = currency;
  const valueEl = el.querySelector('[data-price-value]');
  if (valueEl) {
    valueEl.textContent = allSelected && serverTxt ? serverTxt : formatMinorPrice(minor);
  }
}

function readDeliveryMinor(deliveryValueEl) {
  if (!deliveryValueEl || deliveryValueEl.dataset.rateLoaded !== 'true') {
    return { deliveryMinor: 0, rateLoaded: false };
  }
  if (deliveryValueEl.classList.contains('cart__total-value--free')) {
    return { deliveryMinor: 0, rateLoaded: true };
  }
  if (deliveryValueEl.hasAttribute('data-price-minor')) {
    const deliveryMinorRaw = parseInt(deliveryValueEl.dataset.priceMinor || '0', 10);
    return { deliveryMinor: Number.isNaN(deliveryMinorRaw) ? 0 : deliveryMinorRaw, rateLoaded: true };
  }
  return { deliveryMinor: 0, rateLoaded: true };
}

function pulseCheckboxBox(checkbox) {
  if (!(checkbox instanceof HTMLInputElement)) return;
  const box = checkbox.nextElementSibling;
  if (!(box instanceof HTMLElement) || !box.classList.contains('cart__checkbox-box')) return;
  box.classList.remove('cart__checkbox-box--pulse');
  void box.offsetWidth;
  box.classList.add('cart__checkbox-box--pulse');
  box.addEventListener(
    'animationend',
    function() {
      box.classList.remove('cart__checkbox-box--pulse');
    },
    { once: true }
  );
}

function createCartSelectAllController(form) {
  const selectAll = form.querySelector('.cart__select-all-checkbox');
  const cards = form.querySelectorAll('.cart__card-item');
  const countEl = document.getElementById('cart-selected-count');
  const subtotalEl = document.getElementById('cart-subtotal-value');
  const discountEl = document.getElementById('cart-discount-value');
  const totalEl = document.getElementById('cart-total-value');
  const sidebar = document.getElementById('cart-summary');
  if (!selectAll || !sidebar) return null;

  const getCardCheckboxes = (card) => Array.from(card.querySelectorAll('.cart__card-checkbox'));

  const isCardSelected = (card) => {
    const boxes = getCardCheckboxes(card);
    if (!boxes.length) return true;
    return boxes[0].checked;
  };

  function syncCardCheckboxes(card, checked, source) {
    getCardCheckboxes(card).forEach(function(cb) {
      if (cb !== source) cb.checked = checked;
    });
  }

  function updateSelectAllState() {
    const allSelected =
      cards.length > 0 && Array.from(cards).every(function(card) {
        return isCardSelected(card);
      });
    selectAll.checked = allSelected;
  }

  function updateSummary() {
    const items = Array.from(cards).map(function(card) {
      return { card: card };
    });
    const selected = items.filter(function(x) {
      return isCardSelected(x.card);
    });
    const linesSubtotalMinor = selected.reduce((sum, x) => {
      const minor = parseInt(x.card.dataset.lineTotalMinor, 10);
      return sum + (isNaN(minor) ? 0 : minor);
    }, 0);
    const fullSubtotal = parseInt(sidebar.dataset.cartSubtotalMinor, 10) || 0;
    const fullDiscount = parseInt(sidebar.dataset.cartDiscountMinor, 10) || 0;
    const fullTotal = parseInt(sidebar.dataset.cartTotalMinor, 10) || 0;
    const currency = sidebar.dataset.currency || window.theme?.defaultCurrency || 'AED';
    const allSelected = items.length > 0 && selected.length === items.length;
    const selectedSubtotalMinor = allSelected ? fullSubtotal : linesSubtotalMinor;
    const proratedDiscount =
      fullDiscount <= 0
        ? 0
        : allSelected
          ? fullDiscount
          : fullSubtotal > 0
            ? Math.round((fullDiscount * selectedSubtotalMinor) / fullSubtotal)
            : 0;
    const deliveryValueEl = document.getElementById('cart-delivery-value');
    const { deliveryMinor, rateLoaded } = readDeliveryMinor(deliveryValueEl);
    const totalMinor = (allSelected ? fullTotal : selectedSubtotalMinor - proratedDiscount) + deliveryMinor;

    updateFreeDeliveryProgress(selectedSubtotalMinor, deliveryMinor, rateLoaded, currency);

    if (countEl) countEl.textContent = selected.length;
    const subtotalCountEl = document.getElementById('cart-subtotal-count');
    if (subtotalCountEl) subtotalCountEl.textContent = selected.length;
    const suffixEl = document.getElementById('cart-selected-suffix');
    if (suffixEl) suffixEl.textContent = selected.length === 1 ? ' item' : ' items';

    updatePriceElement(subtotalEl, selectedSubtotalMinor, currency, sidebar.dataset.displaySubtotal, allSelected);
    if (discountEl && fullDiscount > 0) {
      updatePriceElement(discountEl, proratedDiscount, currency, sidebar.dataset.displayDiscount, allSelected);
    }
    if (totalEl) {
      totalEl.setAttribute('value', (totalMinor / 100).toFixed(2));
      updatePriceElement(totalEl, totalMinor, currency, sidebar.dataset.displayTotal, allSelected);
    }

    const mobileCount = document.getElementById('cart-mobile-bar-count');
    const mobileTotal = document.querySelector('[data-cart-mobile-bar] .cart-mobile-bar__total');
    if (mobileCount) mobileCount.textContent = String(selected.length);
    updatePriceElement(mobileTotal, totalMinor, currency, sidebar.dataset.displayTotal, allSelected);
    document.dispatchEvent(new CustomEvent('theme:convert-prices'));
  }

  function onSelectAllChange() {
    cards.forEach(function(card) {
      syncCardCheckboxes(card, selectAll.checked, null);
    });
    pulseCheckboxBox(selectAll);
    updateSummary();
    scheduleCartDeliveryEtaSummaryRefresh();
  }

  function onCardCheckboxChange(event) {
    const card = event.target.closest('.cart__card-item');
    if (!card) return;
    syncCardCheckboxes(card, event.target.checked, event.target);
    getCardCheckboxes(card).forEach(pulseCheckboxBox);
    updateSelectAllState();
    updateSummary();
    scheduleCartDeliveryEtaSummaryRefresh();
  }

  return {
    selectAll,
    cards,
    getCardCheckboxes,
    onSelectAllChange,
    onCardCheckboxChange,
    updateSelectAllState,
    updateSummary
  };
}

export function initCartSelectAll() {
  const form = document.getElementById('cart-form');
  if (!form) return;

  const controller = createCartSelectAllController(form);
  if (!controller) return;

  const { selectAll, cards, getCardCheckboxes, onSelectAllChange, onCardCheckboxChange, updateSelectAllState, updateSummary } = controller;

  selectAll.removeEventListener('change', onSelectAllChange);
  selectAll.addEventListener('change', onSelectAllChange);
  cards.forEach(function(card) {
    getCardCheckboxes(card).forEach(function(cb) {
      cb.removeEventListener('change', onCardCheckboxChange);
      cb.addEventListener('change', onCardCheckboxChange);
    });
  });
  updateSelectAllState();
  updateSummary();
}

function bootCartSelectAll() {
  let initTimer = 0;

  function scheduleInitCartSelectAll() {
    clearTimeout(initTimer);
    initTimer = setTimeout(function() {
      initTimer = 0;
      initCartSelectAll();
    }, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitCartSelectAll);
  } else {
    scheduleInitCartSelectAll();
  }
  document.addEventListener('shopify:section:load', scheduleInitCartSelectAll);
  document.addEventListener('cart:update', scheduleInitCartSelectAll);
}

bootCartSelectAll();
